import { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Check, Copy, Info, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { useAddSeat, useEmailSeatCredentials } from '@/services/queries';
import { ApiError } from '@/services/api/client';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { splitFormErrors, toFormErrors } from '@/shared/lib/forms';
import type { AddSeatResponse } from '@/types';
import { ADDABLE_ROLES, credentialsText, type AddableSeatRole } from './credentials';

/**
 * Claiming one of a licence's unused seats.
 *
 * The other half of `licenses.seat_count` being capacity rather than a
 * headcount: a lab buys forty seats and names one on the day, and this is how
 * the other thirty-nine ever get used. One person at a time, deliberately -
 * they arrive one at a time, and a bulk form for a thing done singly is a
 * form with four empty rows in it every time.
 */

interface FormValues {
  fullName: string;
  email: string;
  role: AddableSeatRole;
}

const schema = Yup.object({
  fullName: Yup.string().trim().required('Enter their name.').max(200),
  email: Yup.string()
    .trim()
    .required('Enter their email address.')
    .email('Enter a valid email address.')
    .max(320),
  // 'owner' deliberately excluded - see ADDABLE_ROLES.
  role: Yup.string().oneOf(['technician', 'viewer']).required(),
});

export function AddSeatDialog({
  organizationId,
  licenseId,
  licenceLabel,
  seatsFree,
  validityLabel,
  open,
  onOpenChange,
}: {
  organizationId: string;
  licenseId: string;
  /** e.g. "Lab Seat" - which of an organization's licences this is. */
  licenceLabel: string;
  seatsFree: number;
  /** The licence's own term, which this seat inherits. Shown, not chosen. */
  validityLabel: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-lg">
        <DialogBody
          organizationId={organizationId}
          licenseId={licenseId}
          licenceLabel={licenceLabel}
          seatsFree={seatsFree}
          validityLabel={validityLabel}
          onOpenChange={onOpenChange}
        />
      </DialogContent>
    </Dialog>
  );
}

function DialogBody({
  organizationId,
  licenseId,
  licenceLabel,
  seatsFree,
  validityLabel,
  onOpenChange,
}: {
  organizationId: string;
  licenseId: string;
  licenceLabel: string;
  seatsFree: number;
  validityLabel: string;
  onOpenChange: (open: boolean) => void;
}) {
  const addSeat = useAddSeat(organizationId, licenseId);
  const emailCredentials = useEmailSeatCredentials(organizationId);
  const [result, setResult] = useState<AddSeatResponse | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [emailed, setEmailed] = useState(false);

  const formik = useFormik<FormValues>({
    initialValues: { fullName: '', email: '', role: 'technician' },
    validationSchema: schema,
    onSubmit: (values, helpers) => {
      addSeat.mutate(
        {
          fullName: values.fullName.trim(),
          email: values.email.trim(),
          role: values.role,
        },
        {
          onSuccess: (added) => {
            setResult(added);
          },
          onError: (error) => {
            if (error instanceof ApiError && error.fieldErrors) {
              const { placed } = splitFormErrors(toFormErrors(error.fieldErrors), values);
              helpers.setErrors(placed);
            }
          },
        },
      );
    },
  });

  const apiError = addSeat.error instanceof ApiError ? addSeat.error : null;

  const error = (field: keyof FormValues): string | undefined =>
    formik.touched[field] ? formik.errors[field] : undefined;

  const banner = (): string | null => {
    if (!apiError) return null;
    if (!apiError.fieldErrors) return apiError.message;
    const { unplaced } = splitFormErrors(toFormErrors(apiError.fieldErrors), formik.values);
    return unplaced.length > 0 ? apiError.message : null;
  };

  const copy = (value: string, label: string) => {
    navigator.clipboard.writeText(value).then(
      () => {
        setCopied(label);
        setTimeout(() => {
          setCopied((current) => (current === label ? null : current));
        }, 2000);
      },
      () => {
        toast.error('Could not copy. Select the text and copy it manually.');
      },
    );
  };

  if (result) {
    return (
      <>
        <DialogHeader className="shrink-0">
          <DialogTitle>Seat claimed</DialogTitle>
          <DialogDescription>
            {result.seatsUsed} of {result.seatCount} seats are now named.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto">
          <div
            role="alert"
            className="flex gap-3 rounded-lg border border-amber-500/50 bg-amber-500/10 p-4"
          >
            <Info className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-500" />
            <p className="text-sm text-muted-foreground">
              Copy these before you leave — the password is not stored in readable form and cannot
              be shown again.
            </p>
          </div>

          <div className="space-y-2 rounded-lg border border-border p-4">
            <p className="text-sm font-medium">{result.seat.fullName}</p>
            <div className="flex items-center gap-2 text-sm">
              <span className="w-20 shrink-0 text-muted-foreground">Email</span>
              <span className="flex-1 truncate font-mono">{result.seat.email}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="w-20 shrink-0 text-muted-foreground">Licence key</span>
              <span className="flex-1 truncate font-mono">{result.seat.licenseKey}</span>
            </div>
            {result.seat.password !== undefined && (
              <div className="flex items-center gap-2 text-sm">
                <span className="w-20 shrink-0 text-muted-foreground">Password</span>
                <span className="flex-1 truncate font-mono">{result.seat.password}</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border pt-4">
          <Button
            type="button"
            variant="outline"
            disabled={emailCredentials.isPending}
            onClick={() => {
              emailCredentials.mutate(
                {
                  seatId: result.seat.seatId,
                  ...(result.seat.password !== undefined ? { password: result.seat.password } : {}),
                },
                {
                  onSuccess: (sent) => {
                    setEmailed(true);
                    toast.success(`Sent to ${sent.toEmail}.`);
                  },
                  onError: (error) => {
                    toast.error(error.message);
                  },
                },
              );
            }}
          >
            {emailed ? <Check className="size-4" /> : <Mail className="size-4" />}
            {emailCredentials.isPending ? 'Sending…' : emailed ? 'Emailed' : 'Email to them'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              copy(credentialsText([result.seat]), 'all');
            }}
          >
            {copied === 'all' ? <Check className="size-4" /> : <Copy className="size-4" />}
            {copied === 'all' ? 'Copied' : 'Copy all'}
          </Button>
          <Button
            type="button"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Done
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <DialogHeader className="shrink-0">
        <DialogTitle>Add a person</DialogTitle>
        <DialogDescription>
          {licenceLabel} · {seatsFree} seat{seatsFree === 1 ? '' : 's'} free
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={formik.handleSubmit} className="flex min-h-0 flex-1 flex-col" noValidate>
        {/* `-mx-1 px-1` so a focused field's 3px ring is not clipped by the
            scroll container - see CreateOrganizationDialog. */}
        <div className="-mx-1 min-h-0 flex-1 space-y-4 overflow-y-auto px-1">
          {banner() && (
            <p
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {banner()}
            </p>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="add-seat-name">Full name</Label>
            <Input
              id="add-seat-name"
              {...formik.getFieldProps('fullName')}
              aria-invalid={Boolean(error('fullName'))}
            />
            {error('fullName') && <p className="text-sm text-destructive">{error('fullName')}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="add-seat-email">Email</Label>
            <Input
              id="add-seat-email"
              type="email"
              {...formik.getFieldProps('email')}
              aria-invalid={Boolean(error('email'))}
            />
            {error('email') && <p className="text-sm text-destructive">{error('email')}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="add-seat-role">Role</Label>
            <Select
              value={formik.values.role}
              onValueChange={(value) => {
                void formik.setFieldValue('role', value);
              }}
            >
              <SelectTrigger id="add-seat-role" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ADDABLE_ROLES.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <p className="text-xs text-muted-foreground">
            They get a licence key of their own and a portal login. The password is generated and
            shown once, on the next screen.
          </p>

          <div className="flex items-baseline justify-between rounded-lg border border-border bg-muted/40 px-3.5 py-2.5 text-xs">
            <span className="text-muted-foreground">Validity, from this licence</span>
            <span className="font-mono font-medium">{validityLabel}</span>
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={addSeat.isPending}>
            {addSeat.isPending ? 'Adding…' : 'Add person'}
          </Button>
        </div>
      </form>
    </>
  );
}
